/**
 * Constantes para el sistema de comisiones
 */

/**
 * Planes disponibles para comisiones
 * Estos códigos deben coincidir con los planes del reporte de ventas
 */
export const PLANES_COMISION = [
  { code: 'FULL', nombre: 'Full' },
  { code: 'GOLD', nombre: 'Gold' },
  { code: 'PLAY', nombre: 'Play' },
  { code: 'PLUS700', nombre: 'Plus 700' },
  { code: 'START800', nombre: 'Start 800' },
  { code: 'AYUDA500', nombre: 'Ayuda 500' },
  { code: 'FAMILY700', nombre: 'Family 700' },
] as const;

export type PlanCode = typeof PLANES_COMISION[number]['code'];

/**
 * Valores predefinidos para los planes de comisión
 */
export const PLANES_PREDEFINIDOS = [
  { code: 'FULL', nombre: 'HOGAR FULL 1000MBPS', precio_unitario: 17.50, activo: true },
  { code: 'GOLD', nombre: 'HOGAR GOLD 1000MBPS', precio_unitario: 29.99, activo: true },
  { code: 'PLAY', nombre: 'HOGAR PLAY 1000MBPS', precio_unitario: 21.99, activo: true },
  { code: 'PLUS700', nombre: 'HOGAR PLUS 700MBPS', precio_unitario: 19.99, activo: true },
  { code: 'START800', nombre: 'HOGAR START 800MBPS', precio_unitario: 17.50, activo: true },
  { code: 'AYUDA500', nombre: 'PLAN AYUDA SOCIAL 500MBPS', precio_unitario: 15.50, activo: true },
  { code: 'FAMILY700', nombre: 'PLAN FAMILY TURBO 700MBPS', precio_unitario: 17.50, activo: true },
  { code: 'GREMIO700', nombre: 'PLAN GREMIOS 700MBPS', precio_unitario: 17.50, activo: true },
  { code: 'SPORT1000', nombre: 'HOGAR SPORTS 1000MBPS', precio_unitario: 24.99, activo: true },
];

/**
 * Valores predefinidos para los tiers de comisión (Asesor/Vendedor)
 * Ciudad null indica que aplica para todas las ciudades
 */
export const TIERS_PREDEFINIDOS = [
  { rol: 'asesor', desde: 1, hasta: 10, factor: 0.20, ciudad: null },
  { rol: 'asesor', desde: 11, hasta: 15, factor: 0.50, ciudad: null },
  { rol: 'asesor', desde: 16, hasta: 9999, factor: 1.00, ciudad: null },
];

/**
 * Valores predefinidos para los bonos por escalón
 */
export const BONOS_PREDEFINIDOS = [
  { rol: 'vendedor', umbral_desde: 12, monto: 100, ciudad: 'CUENCA' },
  { rol: 'vendedor', umbral_desde: 16, monto: 40, ciudad: 'CUENCA' },
  { rol: 'vendedor', umbral_desde: 20, monto: 100, ciudad: 'OTRAS LOCALIDADES' },
  { rol: 'vendedor', umbral_desde: 25, monto: 40, ciudad: 'OTRAS LOCALIDADES' },
  { rol: 'supervisor', umbral_desde: 300, monto: 120, ciudad: 'GUAYAQUIL' },
  { rol: 'supervisor', umbral_desde: 200, monto: 120, ciudad: 'QUITO' },
  { rol: 'supervisor', umbral_desde: 200, monto: 120, ciudad: 'MANTA' },
  { rol: 'supervisor', umbral_desde: 100, monto: 120, ciudad: 'CUENCA' },
];

/**
 * Valores predefinidos para los vales (alimentación y movilización)
 * Ciudad null representa "TODAS" las ciudades
 */
export const VALES_PREDEFINIDOS = [
  { rol: 'vendedor', tipo: 'alimentacion' as const, umbral: 5, monto: 50, ciudad: null },
  { rol: 'vendedor', tipo: 'movilizacion' as const, umbral: 10, monto: 50, ciudad: null },
  { rol: 'supervisor', tipo: 'alimentacion' as const, umbral: 5, monto: 50, ciudad: null },
  { rol: 'supervisor', tipo: 'movilizacion' as const, umbral: 10, monto: 50, ciudad: null },
];

/**
 * Valor predefinido para la configuración de chargeback
 */
export const CHARGEBACK_PREDEFINIDO = {
  monto_unitario: 8.75,
  activo: true,
};

/**
 * Valores predefinidos para tramos de comisión de Jefatura
 */
export const JEFATURA_TRAMOS_PREDEFINIDOS = [
  { desde: 1500, hasta: 2500, porcentaje: 0.025 },
  { desde: 2501, hasta: 3500, porcentaje: 0.0275 },
  { desde: 3501, hasta: 4500, porcentaje: 0.03 },
  { desde: 4501, hasta: 5500, porcentaje: 0.0325 },
];

/**
 * Valor predefinido para ajuste de meta mínima de Jefatura
 */
export const JEFATURA_AJUSTE_META_PREDEFINIDO = {
  meta_minima_contratos: 281,
  monto_por_contrato_faltante: 18.79,
  monto_total_ajuste: 5279.26,
  activo: false,
};

/**
 * Valor predefinido para configuración de Jefatura
 */
export const JEFATURA_CONFIG_PREDEFINIDO = {
  coordinador_general: 'GUILLERMO BASTIDAS',
  valor_base_jefatura: null,
  meta_masivos: 14,
  activo: true,
};

/**
 * Valores predefinidos para tramos de comisión de Líder
 */
export const LIDER_TRAMOS_PREDEFINIDOS = [
  { desde: 1501, hasta: 2500, factor: 0.5, ventas_fijo: 250, capacitacion_fijo: 250 },
  { desde: 2501, hasta: 3500, factor: 0.7, ventas_fijo: 250, capacitacion_fijo: 250 },
  { desde: 3501, hasta: 4500, factor: 0.9, ventas_fijo: 250, capacitacion_fijo: 250 },
  { desde: 4501, hasta: 5500, factor: 1.0, ventas_fijo: 250, capacitacion_fijo: 250 },
];

/**
 * Valores predefinidos para personas de Líder
 */
export const LIDER_PERSONAS_PREDEFINIDAS = [
  'DIAZ AMOGUIMBA SERGIO NICOLAS',
  'RODRIGUEZ GALARZA DAVID RICARDO',
];

/**
 * Valor predefinido para configuración de Líder
 * NOTA: bono_por_contrato ya no se usa en el cálculo (deprecado)
 * NOTA: valor_base_lider siempre es NULL (se calcula automáticamente desde la API)
 */
export const LIDER_CONFIG_PREDEFINIDO = {
  bono_por_contrato: 0,
  valor_base_lider: null,
  activo: true,
};

/**
 * Valores predefinidos para porcentajes de Coordinador
 */
export const COORDINADOR_PORCENTAJES_PREDEFINIDOS = [
  { contratos_desde: 1, contratos_hasta: 10, porcentaje_comision: 10.00, observacion: 'Total de ventas de asesores' },
  { contratos_desde: 11, contratos_hasta: 50, porcentaje_comision: 11.00, observacion: 'Total de ventas de asesores' },
  { contratos_desde: 51, contratos_hasta: 100, porcentaje_comision: 12.00, observacion: 'Total de ventas de asesores' },
  { contratos_desde: 101, contratos_hasta: 300, porcentaje_comision: 13.00, observacion: 'Total de ventas de asesores' },
  { contratos_desde: 301, contratos_hasta: 500, porcentaje_comision: 14.00, observacion: 'Total de ventas de asesores' },
  { contratos_desde: 501, contratos_hasta: 1000, porcentaje_comision: 15.00, observacion: 'Total de ventas de asesores' },
];
